import {computed, makeObservable, observable, action, runInAction} from "mobx";
import BaseModel from "./BaseModel";
import GameTeam from "./GameTeam";
import { sportModules } from "./Sports/registry";
import {post} from "../common/requester";
import Award from "./Award";

/**
 * Backend game_to_json shape (important):
 * {
 *   id, name, start_time (string "dd.mm.YYYY HH:MM"),
 *   address, company_id, sport_id,
 *   company, guest_company, tournament,
 *   game_team_1_id, game_team_1, game_team_2_id, game_team_2,
 *   player_1_id, player_1, player_2_id, player_2,
 *   location, fee,
 *   result, has_media
 * }
 */
export default class Game extends BaseModel {
  constructor(store) {
    super(store);

    makeObservable(this, {
      // primitives / ids
      id: observable,
      name: observable,
      company_id: observable,
      sport_id: observable,
      tournament_id: observable,

      game_team_1_id: observable,
      game_team_2_id: observable,
      player_1_id: observable,
      player_2_id: observable,

      address: observable,
      location: observable,
      start_time: observable,
      start_time_display: observable,
      fee: observable,

      guest_company_id: observable,
      has_media: observable,

      // nested objects (as plain objects or models)
      company: observable,
      guest_company: observable,
      tournament: observable,

      game_team_1: observable,
      game_team_2: observable,
      player_1: observable,
      player_2: observable,

      team_members_1: observable,
      team_members_2: observable,

      // result
      result: observable,
      resultModel: observable,

      // actions
      ensureResultInitialized: action,
      prepareResultForSave: action,

      // computed
      isTeamGame: computed,
      isIndividualGame: computed,
      isFriendlyMatch: computed,
      isValid: computed,
      scoreText: computed,
      winnerText: computed,
      saveResults: action,
      finishGame: action,
      award: observable,
      getAwards: action,
      getAwardTypes: action,
      createAward: action,
      mode: observable,
      setModeFromSport: action,
      isBaseValid: computed,
    });
  }

  // ---------- fields ----------
  id = 0;
  name = "";
  company_id = 0;
  sport_id = ""; // backend uses string (Sport.id)
  tournament_id = 0;

  game_team_1_id = 0;
  game_team_2_id = 0;
  player_1_id = 0;
  player_2_id = 0;

  address = "";
  location = {}; // {lat,lng}
  start_time = new Date(); // you may keep as Date
  start_time_display = '';
  fee = 0;

  company = {}; // {id,name,logo}
  guest_company_id = 0;
  guest_company = {}; // {id,name,logo}
  tournament = null; // {id,name} or null

  game_team_1 = new GameTeam();
  game_team_2 = new GameTeam();

  team_members_1 = [{id: -1, title: '???', forma_number: 1}]
  team_members_2 = [{id: -1, title: '???', forma_number: 1}]

  player_1 = {}; // {id,title}
  player_2 = {}; // {id,title}

  // this is the "result envelope" from backend: game.data.result
  // store it as top-level `result` because your API returns `result` already
  result = null;

  has_media = false;

  // runtime sport-specific model (not from backend directly)
  resultModel = null;
  award = new Award(this)
  mode = 'team'
  
  setModeFromSport(sportId) {
    const sports = this.store?.club?.sport_names || [];
    const sport = sports.find((s) => s.id === sportId);
    const sportType = sport?.data?.type || sport?.type || "team";
  
    this.mode = sportType === "individual" ? "individual" : "team";
  }

  // ---------- parsing ----------
  fromJson(json) {
    if (!json) return;

    // assign direct keys that exist in backend response
    this.id = json.id ?? this.id;
    this.name = json.name ?? this.name;
    this.company_id = json.company_id ?? this.company_id;
    this.sport_id = json.sport_id ?? this.sport_id;
    this.setModeFromSport(this.sport_id);
    this.tournament_id = json.tournament_id ?? this.tournament_id;

    this.game_team_1_id = json.game_team_1_id ?? 0;
    this.game_team_2_id = json.game_team_2_id ?? 0;
    this.player_1_id = json.player_1_id ?? 0;
    this.player_2_id = json.player_2_id ?? 0;

    this.address = json.address ?? this.address;
    this.location = json.location ?? this.location;
    this.fee = json.fee ?? this.fee;

    this.company = json.company ?? this.company;
    this.guest_company_id = json.guest_company_id ?? this.guest_company_id;
    this.guest_company = json.guest_company ?? this.guest_company;
    this.tournament = json.tournament ?? this.tournament;

    this.has_media = Boolean(json.has_media);

    // start_time comes as "dd.mm.YYYY HH:MM"
    this.start_time = parseDdMmYyyyHHmm(json.start_time) ?? this.start_time;
    this.start_time_display = json.start_time;

    // teams (backend returns objects; you can adapt to GameTeam model)
    if (json.game_team_1_id) {
      this.game_team_1.fromJson?.(json.game_team_1)
      this.team_members_1 = json.team_members_1
    }

    if (json.game_team_2_id) {
      this.game_team_2.fromJson?.(json.game_team_2);
      this.team_members_2 = json.team_members_2
    }

    // players are plain objects from backend
    this.player_1 = json.player_1 ?? this.player_1;
    this.player_2 = json.player_2 ?? this.player_2;

    // IMPORTANT: backend already flattens `result` from game.data into top-level `result`
    this.result = json.result ?? this.result;

    // create default result + attach sport-specific model
    this.ensureResultInitialized();
  }

  // ---------- result initialization ----------
  ensureResultInitialized() {
    // If old game had empty game.data, backend returns result = {}
    // Normalize to a stable envelope always.
    if (!this.result || typeof this.result !== "object") this.result = {};

    // Determine sport module
    const mod = sportModules[this.sport_id];

    // Create/repair common envelope
    // Keep your current format but add metadata to support sport-specific
    if (!this.result.schema_version) this.result.schema_version = 1;
    this.result.sport = this.sport_id;

    if (!this.result.final || typeof this.result.final !== "object") {
      this.result.final = {side1: 0, side2: 0};
    } else {
      // repair missing keys
      this.result.final.side1 = this.result.final.side1 ?? 0;
      this.result.final.side2 = this.result.final.side2 ?? 0;
    }

    if (this.result.winner === undefined) this.result.winner = null;
    if (!this.result.status) this.result.status = "draft";

    // Create/repair sport details + bind resultModel
    if (mod) {
      // details object must match the module type
      if (!this.result.details || this.result.details.type !== mod.type) {
        const tmp = new mod.Model(null);
        this.result.details = tmp.toJson();
      }
      this.resultModel = new mod.Model(this.result.details);
    } else {
      this.resultModel = null;
    }
  }

  /**
   * Call this before sending to backend to persist result into game.data.
   * Your backend expects game.data['result'].
   */
  prepareResultForSave() {
    if (!this.result) this.ensureResultInitialized();
    if (!this.resultModel) return;

    // store sport-specific details back into result
    this.result.details = this.resultModel.toJson();

    // recompute final/winner from details (football event log -> score)
    if (typeof this.resultModel.recomputeCommon === "function") {
      this.resultModel.recomputeCommon(this.result);
    }

    // ensure metadata
    this.result.sport = this.sport_id;
    if (!this.result.schema_version) this.result.schema_version = 1;
  }

  // ---------- computed ----------
  get isFriendlyMatch() {
    return !this.tournament_id;
  }

  // Team-vs-team: game_team_1_id and game_team_2_id are set (tournament groups etc)
  get isTeamGame() {
    return Boolean(this.game_team_1_id || this.game_team_2_id);
  }

  // Player-vs-player: player_1_id and player_2_id are set (individual)
  get isIndividualGame() {
    return Boolean(this.player_1_id || this.player_2_id);
  }

  get isValid() {
    if (!this.isBaseValid) return false;
  
    if (this.mode === "individual") {
      return Boolean(this.player_1_id);
    }
  
    return Boolean(this.game_team_1_id);
  }

  get isBaseValid() {
    const hasLocation = this.location && this.location.lat && this.location.lng;
  
    return Boolean(
      this.company_id &&
      this.sport_id &&
      this.name &&
      this.address &&
      this.start_time &&
      hasLocation
    );
  }

  get scoreText() {
    const r = this.result;
    const s1 = r?.final?.side1 ?? 0;
    const s2 = r?.final?.side2 ?? 0;
    return `${s1}:${s2}`;
  }

  get winnerText() {
    const w = this.result?.winner;
    if (!w) return "—";
    if (w === "draw") return "draw";
    return w;
  }

  // ---------- serialization to backend ----------
  /**
   * Your backend currently returns `result` top-level, but when you save
   * you likely send some payload that becomes game.data on backend.
   *
   * If your save endpoint expects { ..., data: { result: ... } }
   * then use toPayload() like this.
   */
  toPayload() {
    this.prepareResultForSave();

    return {
      id: this.id,
      name: this.name,
      start_time: this.start_time, // convert as your API expects
      address: this.address,
      company_id: this.company_id,
      sport_id: this.sport_id,
      tournament_id: this.tournament_id || null,

      game_team_1_id: this.game_team_1_id || null,
      game_team_2_id: this.game_team_2_id || null,
      player_1_id: this.player_1_id || null,
      player_2_id: this.player_2_id || null,

      fee: this.fee,
      data: {
        // IMPORTANT: persist result back into game.data
        result: this.result,
        location: this.location,
      },
    };
  }

  async saveResults() {
    this.prepareResultForSave()
    return post('game/save_results', {game_id: this.id, result: this.result})
  }

  async finishGame() {
    runInAction(() => {
      this.result.status = 'finished'
    })
    return post('game/finish_game', {game_id: this.id})
  }

  async getAwardTypes({sport_id, scope, include_common = true} = {}) {
    console.log('---game award types calling', sport_id, scope, include_common)
    this.award.setTypeFilter({sport_id, scope, include_common});
    return this.award.getAwardTypes();
  }

  async getAwards(filters = {}) {
    // list awards for this parent/child (user_id)
    return this.award.listAwards(filters);
  }

  async createAward(payload = {}) {
    // create award for this user by default
    return this.award.createAward({game_id: this.id, ...payload});
  }
}

// -------- helpers --------
function parseDdMmYyyyHHmm(s) {
  if (!s || typeof s !== "string") return null;
  // "15.02.2026 10:30"
  const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})$/);
  if (!m) return null;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  const HH = Number(m[4]);
  const MM = Number(m[5]);

  const d = new Date(yyyy, mm - 1, dd, HH, MM, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
}